import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { LoadLoyaltyPrograms } from '@features/loyalty-program/store/actions/loyalty-program.actions';
import {
  UpdateLocale,
  UpdateLocalLocale,
} from '@features/user-preference/store/actions/user-preference.actions';
import { getUserPreferences } from '@features/user-preference/store/reducers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AppInit } from '@rd/app-init/app-init.action';
import {
  getAuthHermesLoading,
  getAuthIsLoggedIn,
} from '@rd/core/auth/store/selectors/auth.selectors';
import { AppSettingService } from '@rd/core/services/app-setting/app-setting.service';
import { DomService } from '@rd/core/services/dom/dom.service';
import { getTenantBootstrapLocales } from '@rd/core/tenant/store/selectors/tenant.selectors';
import {
  DEFAULT_LOCALE_CODE,
  SUPPORTED_LOCALES,
} from '@rd/core/tokens/rd-lib.token';
import { HttpError, Locale, UserPreference } from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import { combineLatest, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  takeWhile,
  tap,
} from 'rxjs/operators';
import * as AuthActions from '../../../auth/store/actions/auth.actions';
import { LoadBootstrap } from '../../../tenant/store/actions/tenant.actions';
import * as localeActions from '../actions/locale.actions';

@Injectable()
export class LocaleEffects {
  constructor(
    private store: Store,
    private actions$: Actions,
    private translateService: TranslateService,
    @Inject(DOCUMENT) private document: Document,
    private appSettingService: AppSettingService,
    private domService: DomService,
    @Inject(DEFAULT_LOCALE_CODE) private defaultLocaleCode: string,
    @Inject(SUPPORTED_LOCALES) private supportedLocales: string[],
  ) {}

  loadLocale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(localeActions.LocaleActionTypes.LOAD_LOCALE),
      switchMap((action: localeActions.LoadLocale) => {
        return this.translateService.use(action.payload.code).pipe(
          map(() => new localeActions.LoadLocaleSuccess(action.payload)),
          catchError((error: HttpError) => {
            return of(new localeActions.LoadLocaleFail(error));
          }),
        );
      }),
    ),
  );

  loadLocaleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(localeActions.LocaleActionTypes.LOAD_LOCALE_SUCCESS),
      tap((action: localeActions.LoadLocaleSuccess) => {
        this.setHtmlLang(action.payload);
      }),
      map((action: localeActions.LoadLocaleSuccess) => {
        return new AuthActions.UpdateLocaleCookie(action.payload.code);
      }),
    ),
  );

  changeLocale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(localeActions.LocaleActionTypes.CHANGE_LOCALE),
      switchMap((action: localeActions.ChangeLocale) => {
        return this.translateService.use(action.payload.code).pipe(
          map(() => new localeActions.ChangeLocaleSuccess(action.payload)),
          catchError((error: HttpError) => {
            return of(new localeActions.ChangeLocaleFail(error));
          }),
        );
      }),
    ),
  );

  changeLocaleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(localeActions.LocaleActionTypes.CHANGE_LOCALE_SUCCESS),
      tap((action: localeActions.ChangeLocaleSuccess) => {
        this.setHtmlLang(action.payload);
      }),
      switchMap((action: localeActions.ChangeLocaleSuccess) => {
        return [
          new AuthActions.UpdateLocaleCookie(action.payload.code),
          new LoadBootstrap(),
          new UpdateLocale(action.payload),
          new UpdateLocalLocale(action.payload),
          new LoadLoyaltyPrograms(),
        ];
      }),
    ),
  );

  setDefaultLang$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppInit),
        tap(() => {
          this.translateService.setDefaultLang(this.defaultLocaleCode);
        }),
      ),
    {
      dispatch: false,
    },
  );

  initLocale$ = createEffect(() =>
    combineLatest([
      this.store.select(getTenantBootstrapLocales).pipe(filter(isNotNull)), // Obtained after loading from bootstrap
      this.store.select(getUserPreferences), // Only need this if user is logged in
      this.store.select(getAuthIsLoggedIn), // This returns true, false, null
      this.store.select(getAuthHermesLoading), // This is required for us to know if it's safe to read from user preferences
    ]).pipe(
      // While hermes has not yet completed loading, we need to pull data since anytime any of the observables emit a value,
      // the subscribe run is block
      takeWhile(
        ([, , isLoggedIn, hermesLoading]) =>
          isLoggedIn === null || hermesLoading === true,
        true,
      ),
      // We only want the emission where hermes has finished loading and there could potentially be a user preference
      filter(
        ([, , isLoggedIn, hermesLoading]) =>
          isLoggedIn !== null && hermesLoading === false,
      ),
      take(1),
      map(
        ([tenantLocales, userPreferences, isLoggedIn]: [
          Locale[] | null,
          UserPreference,
          boolean | null,
          boolean,
        ]) => {
          // only load default locale if it's anonymous user
          // we also don't want to load default locale after clicking logout button
          // authIsLoggedIn: null | true | false
          // null: we don't have user info yet
          // true: logged in, locale will be loaded by other subscription
          // false: load default locale
          let localeCode: string;
          if (isLoggedIn === false) {
            localeCode = this.detectLocaleCode();
          } else {
            const userLocaleCode = userPreferences && userPreferences.locale;
            localeCode = this.detectLocaleCode(userLocaleCode);
          }

          const foundLocale: Locale | undefined = tenantLocales?.find(
            (locale: Locale) => locale.code === localeCode,
          );
          return foundLocale;
        },
      ),
      filter(isNotNull),
      map((locale) => new localeActions.LoadLocale(locale)),
    ),
  );

  private detectLocaleCode(userLocaleCode?: string): string {
    const cookie = this.domService.getCookie('ascenda.locale');
    const browserLocaleCode = this.getBrowserLang();

    // userLocaleCode only exists when user is logged in, it will be the first priority
    if (userLocaleCode && this.supportedLocales.includes(userLocaleCode)) {
      return userLocaleCode;
    }

    // The following cases are when user is not logged in
    // with the following priority: cookie, browser language, default tenant setting
    if (cookie && this.supportedLocales.includes(cookie)) {
      return cookie;
    }

    if (
      this.appSettingService.useBrowserLocale &&
      browserLocaleCode &&
      this.supportedLocales.includes(browserLocaleCode)
    ) {
      return browserLocaleCode;
    }

    return this.defaultLocaleCode;
  }

  // copied from https://github.com/ngx-translate/core/blob/master/projects/ngx-translate/core/src/lib/translate.service.ts#L506
  // translateService.getBrowserLang() only return language code instead of locale code
  // e.g. `en` instead of `en-US`
  private getBrowserLang(): string | undefined {
    if (
      typeof window === 'undefined' ||
      typeof window.navigator === 'undefined'
    ) {
      return undefined;
    }

    let browserLang: any = window.navigator.languages
      ? window.navigator.languages[0]
      : null;
    browserLang =
      browserLang ||
      window.navigator.language ||
      // @ts-ignore TS2339: Property 'browserLanguage' does not exist on type 'Navigator'.
      window.navigator.browserLanguage ||
      // @ts-ignore TS2339: Property 'userLanguage' does not exist on type 'Navigator'.
      window.navigator.userLanguage;
    if (typeof browserLang === 'undefined') {
      return undefined;
    }

    return browserLang;
  }

  private setHtmlLang(locale: Locale): void {
    this.document.documentElement.lang = locale.code;
  }
}
